import { lazy, useEffect, useMemo } from "react";
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router-dom";
import "./assets/css/styles.css";
import PersistLogin from "./components/PersistLogin";
import RequireAuth from "./components/RequireAuth";
import AdminShell from "./layout/AdminShell";
import { CAPABILITY } from "./auth/permissions";
import { getDefaultPathForRole } from "./app/navigation";
import useAuth from "./hooks/useAuth";
import Unauthorized from "./views/Unauthorized";
import NotPermitted from "./views/NotPermitted";
import Login from "./pages/login";

// Route-level code splitting: every view loads on demand so the initial
// bundle only carries the shell. The Suspense boundary lives INSIDE the shell
// (AdminShell), so a chunk load shows a content skeleton rather than
// replacing the whole page.
const Dashboard = lazy(() => import("./views/Dashboard"));
const Transactions = lazy(() => import("./views/Transactions/Transactions"));
const Users = lazy(() => import("./views/User/Users"));
const AddUser = lazy(() => import("./views/User/AddUser"));
const UserDetails = lazy(() => import("./views/User/UserDetails"));
const UpdatePassword = lazy(() => import("./views/User/UpdatePassword"));
const Reports = lazy(() => import("./views/Reports/Reports"));
const Airlines = lazy(() => import("./views/Airline/Airlines"));
const AddAirline = lazy(() => import("./views/Airline/AddAirline"));
const AirlineDetailes = lazy(() => import("./views/Airline/AirlineDetailes"));
const Agencies = lazy(() => import("./views/Agency/Agencies"));
const AddAgency = lazy(() => import("./views/Agency/AddAgency"));
const AgencyDetails = lazy(() => import("./views/Agency/AgencyDetails"));
const Currencies = lazy(() => import("./views/Currency/Currencies"));
const AddCurrency = lazy(() => import("./views/Currency/AddCurrency"));
const CurrencyDetails = lazy(() => import("./views/Currency/CurrencyDetails"));
const Ads = lazy(() => import("./views/Ads/Ads"));
const AddAd = lazy(() => import("./views/Ads/AddAd"));
const AdDetails = lazy(() => import("./views/Ads/AdDetails"));
const ActionLogs = lazy(() => import("./views/Logs/ActionLogs"));
const AutoBookRequests = lazy(() => import("./views/AutoBook/AutoBookRequests"));
const CentralAI = lazy(() => import("./views/AI/CentralAI"));
const TopUps = lazy(() => import("./views/TopUps/TopUps"));
const FundRequests = lazy(() => import("./views/FundRequests/FundRequests"));
const FxRates = lazy(() => import("./views/FxRates/FxRates"));
const AirlineCredit = lazy(() => import("./views/AirlineCredit/AirlineCredit"));

// Disables browser autofill on admin data-entry forms (user/agency creation
// must never be pre-filled with the operator's own credentials). The login
// form opts out with data-allow-autofill so password managers keep working.
const useAutofillGuard = () => {
  useEffect(() => {
    const isExempt = (element) => Boolean(element?.closest?.("[data-allow-autofill]"));

    const disableAutofill = (root) => {
      if (!root || !root.querySelectorAll) return;
      root.querySelectorAll("form").forEach((formElement) => {
        if (isExempt(formElement)) return;
        formElement.setAttribute("autocomplete", "off");
      });
      root.querySelectorAll("input, textarea, select").forEach((field) => {
        if (isExempt(field)) return;
        const fieldType = (field.getAttribute("type") || "").toLowerCase();
        field.setAttribute("autocomplete", fieldType === "password" ? "new-password" : "off");
        field.setAttribute("autocapitalize", "off");
        field.setAttribute("autocorrect", "off");
        field.setAttribute("spellcheck", "false");
      });
    };

    disableAutofill(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches("form, input, textarea, select")) {
            disableAutofill(node.parentElement || node);
          } else {
            disableAutofill(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
};

// Signed-in users landing on /login are sent to their workspace.
const LoginRoute = () => {
  const { isAuthenticated, isBootstrapping, role } = useAuth();
  const location = useLocation();
  if (isBootstrapping) return null;
  if (isAuthenticated) {
    const from = location.state?.from?.pathname;
    const target = from && from !== "/login" ? from : getDefaultPathForRole(role);
    return <Navigate to={target === "/login" ? "/unauthorized" : target} replace />;
  }
  return <Login />;
};

const RootShell = () => {
  useAutofillGuard();
  return <AdminShell />;
};

const guarded = (capability, children) => ({
  element: <RequireAuth capability={capability} />,
  children,
});

export const routes = [
  { path: "/login", element: <LoginRoute /> },
  { path: "/unauthorized", element: <Unauthorized /> },
  {
    element: <PersistLogin />,
    children: [
      {
        element: <RequireAuth capability={CAPABILITY.PANEL_ACCESS} fallback={<Unauthorized />} />,
        children: [
          {
            path: "/",
            element: <RootShell />,
            children: [
              guarded(CAPABILITY.DASHBOARD_VIEW, [{ index: true, element: <Dashboard /> }]),
              guarded(CAPABILITY.REPORTS_VIEW, [{ path: "Reports", element: <Reports /> }]),
              guarded(CAPABILITY.PROFILE_PASSWORD, [{ path: "Profile/Password", element: <UpdatePassword /> }]),

              guarded(CAPABILITY.AUTOBOOK_VIEW, [{ path: "AutoBook", element: <AutoBookRequests /> }]),
              guarded(CAPABILITY.TOPUPS_VIEW, [{ path: "TopUps", element: <TopUps /> }]),
              guarded(CAPABILITY.FUND_REQUESTS_VIEW, [{ path: "FundRequests", element: <FundRequests /> }]),
              guarded(CAPABILITY.TRANSACTIONS_VIEW, [{ path: "Transactions", element: <Transactions /> }]),
              guarded(CAPABILITY.AIRLINE_CREDIT_VIEW, [{ path: "AirlineCredit", element: <AirlineCredit /> }]),

              guarded(CAPABILITY.AGENCIES_VIEW, [
                { path: "Agencies", element: <Agencies /> },
                { path: "Agencies/:id", element: <AgencyDetails /> },
              ]),
              guarded(CAPABILITY.AGENCIES_MANAGE, [{ path: "Agencies/AddAgency", element: <AddAgency /> }]),

              guarded(CAPABILITY.USERS_VIEW, [
                { path: "Users", element: <Users /> },
                { path: "Users/:id", element: <UserDetails /> },
              ]),
              guarded(CAPABILITY.USERS_MANAGE, [{ path: "Users/AddUser", element: <AddUser /> }]),

              guarded(CAPABILITY.AIRLINES_VIEW, [
                { path: "Airlines", element: <Airlines /> },
                { path: "Airlines/:id", element: <AirlineDetailes /> },
              ]),
              guarded(CAPABILITY.AIRLINES_MANAGE, [{ path: "Airlines/AddAirline", element: <AddAirline /> }]),

              guarded(CAPABILITY.CURRENCIES_VIEW, [
                { path: "Currencies", element: <Currencies /> },
                { path: "Currencies/:id", element: <CurrencyDetails /> },
              ]),
              guarded(CAPABILITY.CURRENCIES_MANAGE, [{ path: "Currencies/AddCurrency", element: <AddCurrency /> }]),

              guarded(CAPABILITY.ADS_MANAGE, [
                { path: "Ads", element: <Ads /> },
                { path: "Ads/AddAd", element: <AddAd /> },
                { path: "Ads/:id", element: <AdDetails /> },
              ]),

              guarded(CAPABILITY.PLATFORM_SETTINGS_VIEW, [{ path: "AI", element: <CentralAI /> }]),
              guarded(CAPABILITY.FX_RATES_VIEW, [{ path: "FxRates", element: <FxRates /> }]),
              guarded(CAPABILITY.LOGS_VIEW, [{ path: "Logs", element: <ActionLogs /> }]),

              { path: "*", element: <NotPermitted notFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const createAppRouter = (options) => createBrowserRouter(routes, options);

function App({ router }) {
  const appRouter = useMemo(() => router || createAppRouter(), [router]);
  return <RouterProvider router={appRouter} />;
}

export default App;
